import { determinePaginationParams, getResource, postgRESTApiUrl } from "./api";

const getAllForProfileIdForDisplay = async (profileId, options = { page: 1, per_page: 10 }) => {
  const paginationParams = determinePaginationParams(options);
  const url = `${postgRESTApiUrl()}/rest/v1/derived_tags?is_added=eq.true&profile_id=eq.${profileId}&${paginationParams}`;
  return getResource(url);
};

const getAllForTaggedForDisplay = async (taggedId, taggedType, options = { page: 1, per_page: 10 }) => {
  const paginationParams = determinePaginationParams(options);
  const url =
    `${postgRESTApiUrl()}/rest/v1/derived_tags?is_added=eq.true&tagged_id=eq.${taggedId}` +
    `&tagged_type=eq.${taggedType}&${paginationParams}`;

  return getResource(url);
};

// is_added=eq.true everywhere a display reads tags: a manual tag row with is_added=false is an
// admin's explicit REMOVAL of that tag (it overrides auto-tagging), and the search index honors
// it — so a removed tag rendered as a chip would link to a directory filter with zero matches.
const getAllPrProfileDerivedTags = async (prProfileId) =>
  getResource(
    `${postgRESTApiUrl()}/rest/v1/derived_tags?is_added=eq.true` +
      `&profile_id=eq.${prProfileId}&profile_type=eq.Id::Profile::Promoter`,
  );

const getAllPrProfileTags = async (prProfileId) =>
  getResource(
    `${postgRESTApiUrl()}/rest/v1/tags?is_added=eq.true` +
      `&tagged_id=eq.${prProfileId}&tagged_type=eq.Id::Profile::Promoter`,
  );

const getAllMediaProfileDerivedTags = async (mediaProfileId) =>
  getResource(
    `${postgRESTApiUrl()}/rest/v1/derived_tags?is_added=eq.true` +
      `&profile_id=eq.${mediaProfileId}&profile_type=eq.Id::Profile::MediaProfile`,
  );

const getAllExpertDerivedTags = async (expertSlug) =>
  getResource(
    `${postgRESTApiUrl()}/rest/v1/derived_tags?promo_entity_slug=eq.${expertSlug}` +
      `&promo_entity_type=eq.Id::PromoEntity::Expert&is_added=eq.true`,
  );

// PostgREST truncates any response at max_rows, so a single request can silently drop rows. Shared
// by every batched derived-tags reader below, regardless of which axis it selects on.
const derivedTagsPageSize = 1000;

// `derived_tags` has no single unique key to page on, but the materialized view's own dedupe
// guarantees (profile_id, tag_model_type, tag_model_id) is unique per row, so this tuple is a
// total order and pages can neither overlap nor skip.
const promoEntityTagsOrder = "promo_entity_id,hashtag_id,tag_model_type,tag_model_id,profile_id";

// Every displayable tag for a set of promo entities of one type, in one read rather than one per
// entity. The view fans a tag out per tagged object and per representing profile, so both the entity
// count and the per-entity fan-out are unbounded — walk every page and let callers group by
// promo_entity_id, dedupe by hashtag_id and rank by how often each was tagged.
const getAllForPromoEntitiesForDisplay = async (promoEntityIds, promoEntityType) => {
  // An empty set would build `promo_entity_id=in.()`, which PostgREST rejects — and a page with no
  // represented entities on it is ordinary, not exceptional. Resolving the same `{ data }` shape
  // means callers keep one code path.
  if (!promoEntityIds || promoEntityIds.length === 0) return { data: [] };

  const selector =
    `is_added=eq.true&promo_entity_type=eq.${encodeURIComponent(promoEntityType)}` +
    `&promo_entity_id=in.(${promoEntityIds.join(",")})&order=${promoEntityTagsOrder}`;

  const rows = [];
  let page = 1;
  let pageRowCount = derivedTagsPageSize;

  while (pageRowCount === derivedTagsPageSize) {
    const paginationParams = determinePaginationParams({ page, per_page: derivedTagsPageSize });
    // eslint-disable-next-line no-await-in-loop -- each page's offset follows from the previous one
    const response = await getResource(`${postgRESTApiUrl()}/rest/v1/derived_tags?${selector}&${paginationParams}`);
    if (response.error) return response;

    const pageRows = Array.isArray(response.data) ? response.data : [];
    rows.push(...pageRows);
    pageRowCount = pageRows.length;
    page += 1;
  }

  return { data: rows };
};

// The same total order as `promoEntityTagsOrder`, headed by the axis this method selects on instead.
const profileTagsOrder = "profile_id,hashtag_id,tag_model_type,tag_model_id,promo_entity_id";

// The profile-axis sibling of `getAllForPromoEntitiesForDisplay` — same empty-id guard, same
// page-walking loop, selecting on `profile_id`/`profile_type` instead of the promo-entity columns.
// A roster mixing both axes' person types in one page needs one batched read per axis; this is the
// other one. Dedupe/ranking is the caller's job, exactly as it is for the promo-entity method.
const getAllForProfileIdsForDisplay = async (profileIds, profileType) => {
  if (!profileIds || profileIds.length === 0) return { data: [] };

  const selector =
    `is_added=eq.true&profile_type=eq.${encodeURIComponent(profileType)}` +
    `&profile_id=in.(${profileIds.join(",")})&order=${profileTagsOrder}`;

  const rows = [];
  let page = 1;
  let pageRowCount = derivedTagsPageSize;

  while (pageRowCount === derivedTagsPageSize) {
    const paginationParams = determinePaginationParams({ page, per_page: derivedTagsPageSize });
    // eslint-disable-next-line no-await-in-loop -- each page's offset follows from the previous one
    const response = await getResource(`${postgRESTApiUrl()}/rest/v1/derived_tags?${selector}&${paginationParams}`);
    if (response.error) return response;

    const pageRows = Array.isArray(response.data) ? response.data : [];
    rows.push(...pageRows);
    pageRowCount = pageRows.length;
    page += 1;
  }

  return { data: rows };
};

export default {
  getAllExpertDerivedTags,
  getAllForPromoEntitiesForDisplay,
  getAllForProfileIdForDisplay,
  getAllForProfileIdsForDisplay,
  getAllForTaggedForDisplay,
  getAllMediaProfileDerivedTags,
  getAllPrProfileDerivedTags,
  getAllPrProfileTags,
};
